import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Check, X as XIcon, Undo2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Requests — friend request inbox/outbox. Two tabs (incoming / outgoing),
 * chronological (newest first). Accept / Decline on incoming; Withdraw on
 * outgoing. No counts, no ranking.
 */
const TABS = [
  { key: "incoming", label: "Received", testId: "requests-tab-incoming" },
  { key: "outgoing", label: "Sent",     testId: "requests-tab-outgoing" },
];

export default function Requests() {
  const [tab, setTab] = useState("incoming");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/commons/friend-requests", { params: { direction: tab } });
      setRequests(r.data.requests || []);
    } catch (e) {
      console.error("[requests] load failed:", e);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  async function act(id, verb) {
    if (busyId) return;
    setBusyId(id);
    try {
      if (verb === "accept") await api.post(`/commons/friend-requests/${id}/accept`);
      else if (verb === "decline") await api.post(`/commons/friend-requests/${id}/decline`);
      else if (verb === "cancel") await api.delete(`/commons/friend-requests/${id}`);
      toast.success(
        verb === "accept" ? "friend added" : verb === "decline" ? "declined" : "withdrawn"
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not update request");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-24" data-testid="requests-page">
      <Link
        to="/commons"
        data-testid="requests-back"
        className="inline-flex items-center gap-2 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
      >
        <ArrowLeft size={12} strokeWidth={1.5} />
        back to the commons
      </Link>

      <header className="text-center mt-8">
        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          the mail slot
        </p>
        <h1 className="mt-3 font-serif-lit text-3xl sm:text-5xl text-[var(--cream)] leading-[1.05]">
          friend requests
        </h1>
        <p className="mt-3 font-serif-lit italic text-[var(--cream-dim)] text-base sm:text-lg">
          newest first, always
        </p>
      </header>

      {/* Tabs */}
      <div className="mt-8 flex items-stretch gap-1 sm:gap-2 border border-[rgba(179,137,90,0.16)] rounded-full p-1 bg-[var(--bg-panel)]" role="tablist">
        {TABS.map(({ key, label, testId }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              data-testid={testId}
              className={
                "flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full transition-colors " +
                (active
                  ? "bg-[var(--brass)] text-[var(--bg)]"
                  : "text-[var(--cream-dim)] hover:text-[var(--brass)]")
              }
            >
              <span className="font-mono-lc text-[10px] sm:text-[11px] tracking-[0.16em] uppercase">
                {label}
              </span>
            </button>
          );
        })}
      </div>

      <section className="mt-8">
        {loading ? (
          <p className="text-center py-12 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)]" data-testid="requests-loading">
            checking the mail…
          </p>
        ) : requests.length === 0 ? (
          <div className="text-center py-12" data-testid="requests-empty">
            <p className="font-serif-lit text-xl sm:text-2xl text-[var(--cream)]">
              {tab === "incoming" ? "no requests waiting" : "you haven't sent any requests"}
            </p>
            <p className="mt-3 font-garamond italic text-[var(--cream-dim)] max-w-md mx-auto">
              {tab === "incoming"
                ? "when someone sends you a friend request, it will land here."
                : "browse the directory to find people you know."}
            </p>
            {tab === "outgoing" && (
              <Link to="/commons/directory" className="mt-6 lc-btn inline-flex" data-testid="requests-goto-directory">
                open the directory
              </Link>
            )}
          </div>
        ) : (
          <ul className="space-y-3">
            {requests.map(r => (
              <li key={r.id} className="placed-card p-4 sm:p-5" data-testid={`request-row-${r.id}`}>
                <div className="flex items-start gap-4">
                  <Link
                    to={r.other?.username ? `/u/${r.other.username}` : "#"}
                    className="shrink-0"
                    data-testid={`request-avatar-${r.id}`}
                  >
                    {r.other?.picture ? (
                      <img src={r.other.picture} alt="" className="w-12 h-12 rounded-full object-cover border border-[rgba(179,137,90,0.3)]" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.3)] flex items-center justify-center font-serif-lit text-lg text-[var(--brass)]">
                        {(r.other?.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={r.other?.username ? `/u/${r.other.username}` : "#"}
                      className="font-serif-lit text-[var(--cream)] text-base hover:text-[var(--brass)]"
                      data-testid={`request-name-${r.id}`}
                    >
                      {r.other?.name || "friend"}
                    </Link>
                    {r.other?.username && (
                      <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)]">
                        @{r.other.username}
                      </p>
                    )}
                    {r.message && (
                      <p className="mt-2 font-garamond italic text-[var(--cream-dim)]" data-testid={`request-message-${r.id}`}>
                        "{r.message}"
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  {tab === "incoming" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => act(r.id, "decline")}
                        disabled={busyId === r.id}
                        data-testid={`request-decline-${r.id}`}
                        className="lc-btn-ghost inline-flex items-center gap-2"
                      >
                        <XIcon size={12} strokeWidth={1.6} />
                        decline
                      </button>
                      <button
                        type="button"
                        onClick={() => act(r.id, "accept")}
                        disabled={busyId === r.id}
                        data-testid={`request-accept-${r.id}`}
                        className="lc-btn inline-flex items-center gap-2"
                      >
                        <Check size={12} strokeWidth={1.8} />
                        accept
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => act(r.id, "cancel")}
                      disabled={busyId === r.id}
                      data-testid={`request-cancel-${r.id}`}
                      className="lc-btn-ghost inline-flex items-center gap-2"
                    >
                      <Undo2 size={12} strokeWidth={1.6} />
                      withdraw
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
